import React from 'react';
import {
  Flex,
  Box,
  Table,
  Tbody,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  IconButton,
} from '@chakra-ui/react';
import {
  createColumnHelper,
} from '@tanstack/react-table';
import PageTable from 'components/layout/PageTable';
import AdminTableCell from 'components/layout/AdminTableCell';
import SortableTableHeader from 'components/layout/SortableTableHeader';
import { useSortableAdminTable } from 'hooks/useSortableAdminTable';
import { getSortableColumnOptions } from 'utils/tableColumnOptions';
import TablePagination from 'components/layout/TablePagination';
import ColorsEditModal from './ColorsEditModal';
import DeleteModal from '../DeleteModal/DeleteModal';

const columnHelper = createColumnHelper();

export default function ColorsTable({ columnsData, data, page, setPage, totalPages, pageSize, setPageSize, handleUpdateColor, sorting, onSortingChange }) {
  const [selectedColor, setSelectedColor] = React.useState(null);
  const [modalState, setModalState] = React.useState({ isOpen: false });
  const [colorToDelete, setColorToDelete] = React.useState(null);
  const [deleteModalState, setDeleteModalState] = React.useState({ isOpen: false });
  
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const columns = React.useMemo(() => createColumns(columnsData), [columnsData]);

  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');

  const table = useSortableAdminTable({
    data,
    columns,
    sorting,
    onSortingChange,
  });

  const openEditModal = (rowData) => {
    setSelectedColor(rowData);
    setModalState({ isOpen: true });
  };

  const closeEditModal = () => {
    setModalState({ isOpen: false });
    setSelectedColor(null);
  };

  const openDeleteModal = (rowData) => {
    setColorToDelete(rowData);
    setDeleteModalState({ isOpen: true });
  };

  const closeDeleteModal = () => {
    setDeleteModalState({ isOpen: false });
    setColorToDelete(null);
  };

  const handleDelete = () => {
    const updatedColor = { ...colorToDelete, enabled: !colorToDelete.enabled };
    handleUpdateColor(updatedColor);
    closeDeleteModal();
  };

  function createColumns(columnsData) {
    return columnsData.map((col) =>
      columnHelper.accessor(col.accessor, {
        ...getSortableColumnOptions(col),
        header: () => (
          <Text fontSize={{ sm: '10px', lg: '12px' }}>{col.Header}</Text>
        ),
        cell: (info) => (
          <Flex align="center">
            <Text fontSize="sm" fontWeight="normal">
              {info.getValue()}
            </Text>
            {col.actions &&
              col.actions.map((action, index) => (
                <IconButton
                  key={index}
                  aria-label={action.label}
                  icon={action.icon}
                  size="sm"
                  variant="ghost"
                  colorScheme={action.colorScheme}
                  ml={2}
                  onClick={() => {
                    if (action.type === 'edit') {
                      openEditModal(info.row.original);
                    }
                    else if(action.type === 'delete') {
                      openDeleteModal(info.row.original);
                    }
                  }}
                />
              ))}
          </Flex>
        ),
      }),
    );
  }

  return (
    <PageTable flexDirection="column" w="100%" px="0px" >
      <Box>
        <PageTable.Scroll>
          <Table variant="simple" color="gray.700" mb="24px" mt="12px">
                    <Thead>
                      {table.getHeaderGroups().map((headerGroup) => (
                        <Tr key={headerGroup.id}>
                          {headerGroup.headers.map((header) => (
                            <SortableTableHeader
                              key={header.id}
                              header={header}
                              borderColor={borderColor}
                            />
                          ))}
                        </Tr>
                      ))}
                    </Thead>
                    <Tbody>
                      {table.getRowModel().rows.map((row) => (
                        <Tr key={row.id}>
                          {row.getVisibleCells().map((cell) => (
                                <AdminTableCell key={cell.id} cell={cell} />
                              ))}
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
        </PageTable.Scroll>
          <TablePagination
            page={page}
            setPage={setPage}
            totalPages={totalPages}
            pageSize={pageSize}
            setPageSize={setPageSize}
          />
      </Box>

      <ColorsEditModal
        isOpen={modalState.isOpen}
        onClose={closeEditModal}
        color={selectedColor}
        onUpdate={handleUpdateColor}
      />
      <DeleteModal
        isOpen={deleteModalState.isOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
        name={`${colorToDelete?.code} - ${colorToDelete?.name}`}
      />
    </PageTable>
  );
}